import { useEffect, useRef } from "react";
import styles from "./Modal.module.scss";

// ─── Base Modal ───────────────────────────────────────────────────────────────

export const Modal = ({ open, onClose, children, size = "md" }) => {
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className={styles.overlay}
      ref={overlayRef}
      onClick={(e) => e.target === overlayRef.current && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div className={`${styles.modal} ${styles[`modal--${size}`]}`}>
        {children}
      </div>
    </div>
  );
};

export const ModalHeader = ({ title, subtitle, icon, onClose }) => (
  <div className={styles.header}>
    <div className={styles.headerLeft}>
      {icon && <span className={styles.headerIcon}>{icon}</span>}
      <div>
        <h2 className={styles.title}>{title}</h2>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>
    </div>
    {onClose && (
      <button className={styles.closeBtn} onClick={onClose} aria-label="Fechar">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    )}
  </div>
);

export const ModalBody = ({ children }) => (
  <div className={styles.body}>{children}</div>
);

export const ModalFooter = ({ children }) => (
  <div className={styles.footer}>{children}</div>
);

// ─── Field ────────────────────────────────────────────────────────────────────

export const Field = ({ label, children, hint }) => (
  <div className={styles.field}>
    <label className={styles.label}>{label}</label>
    {children}
    {hint && <span className={styles.hint}>{hint}</span>}
  </div>
);

export const Input = ({ ...props }) => (
  <input className={styles.input} {...props} />
);

export const Select = ({ children, ...props }) => (
  <select className={styles.select} {...props}>{children}</select>
);

export const Textarea = ({ ...props }) => (
  <textarea className={styles.textarea} {...props} />
);

// ─── Buttons ──────────────────────────────────────────────────────────────────

export const Btn = ({ variant = "secondary", children, ...props }) => (
  <button className={`${styles.btn} ${styles[`btn--${variant}`]}`} type="button" {...props}>
    {children}
  </button>
);

// ─── Alert Modal ──────────────────────────────────────────────────────────────

export const AlertModal = ({ open, onClose, type = "info", title, text, confirmText = "OK" }) => (
  <Modal open={open} onClose={onClose} size="sm">
    <ModalBody>
      <div className={styles.alertContent}>
        <div className={`${styles.alertIcon} ${styles[`alertIcon--${type}`]}`}>
          {type === "success" && (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
          {type === "error" && (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          )}
          {type === "warning" && (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          )}
          {type === "question" && (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          )}
        </div>
        <h3 className={styles.alertTitle}>{title}</h3>
        {text && <p className={styles.alertText}>{text}</p>}
      </div>
    </ModalBody>
    <ModalFooter>
      <Btn variant={type === "error" ? "danger" : type === "warning" ? "warning" : "primary"} onClick={onClose}>
        {confirmText}
      </Btn>
    </ModalFooter>
  </Modal>
);

// ─── Confirm Modal ────────────────────────────────────────────────────────────

export const ConfirmModal = ({
  open, onClose, onConfirm,
  type = "warning", title, text,
  confirmText = "Confirmar", cancelText = "Cancelar",
}) => (
  <Modal open={open} onClose={onClose} size="sm">
    <ModalBody>
      <div className={styles.alertContent}>
        <div className={`${styles.alertIcon} ${styles[`alertIcon--${type}`]}`}>
          {type === "warning" && (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          )}
          {type === "question" && (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          )}
          {type === "danger" && (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          )}
        </div>
        <h3 className={styles.alertTitle}>{title}</h3>
        {text && <p className={styles.alertText}>{text}</p>}
      </div>
    </ModalBody>
    <ModalFooter>
      <Btn variant="secondary" onClick={onClose}>{cancelText}</Btn>
      <Btn variant={type === "danger" ? "danger" : type === "warning" ? "warning" : "primary"} onClick={() => { onConfirm(); onClose(); }}>
        {confirmText}
      </Btn>
    </ModalFooter>
  </Modal>
);